import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import NavBar from '../components/NavBar';
import { useAuth } from '../lib/auth';
import { authAPI, gethinhAPI, formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Download, Wallet, History, Sparkles, ArrowRight, Package, Ruler, MailCheck } from 'lucide-react';

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [verifySending, setVerifySending] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    let cancelled = false;
    gethinhAPI.getPricing()
      .then(r => { if (!cancelled) setPricing(r.data); })
      .catch(() => {});
    gethinhAPI.getHistory()
      .then(r => { if (!cancelled) setRecent(r.data.slice(0, 5)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const pricingEntries = useMemo(() => {
    if (!pricing?.pricing_tl) return [];
    return Object.entries(pricing.pricing_tl).filter(([k]) => k !== 'default');
  }, [pricing]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <NavBar />
      <div className="max-w-7xl mx-auto px-4 py-10">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="dashboard-welcome">
            Hos geldin, <span className="bg-gradient-to-r from-fuchsia-400 to-indigo-400 bg-clip-text text-transparent">{user?.name}</span>
          </h1>
          <p className="text-muted-foreground mt-1">Grafik tasarim projelerin icin premium kaynaklari kesfet.</p>
        </div>
        {user && user.role !== 'admin' && !user.email_verified && (
          <div className="aurora-card rounded-3xl p-5 mb-8 border-amber-500/30 bg-amber-500/10 flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <MailCheck className="w-6 h-6 text-amber-400 mt-0.5" />
              <div>
                <h2 className="font-semibold text-amber-100">E-posta onayi gerekli</h2>
                <p className="text-sm text-amber-100/80">Indirme, bakiye yukleme ve olceklendirme islemleri icin e-posta adresinizi onaylayin.</p>
              </div>
            </div>
            <Button variant="outline" disabled={verifySending} className="border-amber-500/40 text-amber-200" onClick={async()=>{ setVerifySending(true); try { await authAPI.resendVerification(); toast.success('Onay e-postasi basariyla mail kutunuza gonderildi!'); await refreshUser(); } catch(e) { toast.error(formatApiError(e)); } finally { setVerifySending(false); } }}>
              {verifySending ? 'Gonderiliyor...' : 'Onay E-postasi Gonder'}
            </Button>
          </div>
        )}
        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <StatCard
            icon={Wallet}
            label="Mevcut Bakiye"
            value={`${(user?.balance_tl || 0).toFixed(2)} TL`}
            cta="Bakiye Yukle"
            ctaTo="/balance"
            gradient="from-emerald-500/20 to-teal-500/20"
            iconColor="text-emerald-400"
            testid="stat-balance"
          />
          <StatCard
            icon={Download}
            label="Premium Indir"
            value="Link yapistir"
            cta="Hemen Indir"
            ctaTo="/search"
            gradient="from-fuchsia-500/20 to-purple-500/20"
            iconColor="text-fuchsia-400"
            testid="stat-download"
          />
          <StatCard
            icon={History}
            label="Indirme Gecmisi"
            value={`${recent.length}+ dosya`}
            cta="Tumunu Gor"
            ctaTo="/history"
            gradient="from-indigo-500/20 to-blue-500/20"
            iconColor="text-indigo-400"
            testid="stat-history"
          />
        </div>

        {/* Quick action */}
        <div className="grid md:grid-cols-2 gap-4 mb-10">
          <div className="aurora-card rounded-3xl p-8">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-fuchsia-500/20 border border-fuchsia-500/30 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-fuchsia-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Premium Gorsel Indirme</h2>
                <p className="text-sm text-muted-foreground">Freepik, Shutterstock, Envato, Adobe Stock gibi saglayicilardan tek linkle indir.</p>
              </div>
            </div>
            <Link to="/search" data-testid="cta-go-search">
              <Button className="bg-gradient-to-r from-fuchsia-500 to-indigo-500 hover:from-fuchsia-600 hover:to-indigo-600">
                Hemen Basla <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          <div className="aurora-card rounded-3xl p-8">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center">
                <Ruler className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Olcu Numune Araci <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 ml-1">Ucretsiz</span></h2>
                <p className="text-sm text-muted-foreground">Gorselinin etrafina cm cinsinden olcu oklari ekle, musteriye gosterimlik numune olustur.</p>
              </div>
            </div>
            <Link to="/tools/resize" data-testid="cta-go-resize">
              <Button variant="outline" className="border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10">
                Araci Kullan <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Pricing */}
        {pricing && (
          <div className="aurora-card rounded-3xl p-6 mb-10">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-4 h-4 text-fuchsia-400" />
              <h3 className="font-semibold">Saglayici Fiyatlandirmasi</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2 text-sm">
              {pricingEntries.map(([k,v]) => (
                <div key={k} className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/5 border border-white/5" data-testid={`pricing-${k}`}>
                  <span className="capitalize text-muted-foreground">{k}</span>
                  <span className="font-semibold text-emerald-400 tabular-nums">{v} TL</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">* Fiyatlar indirme basina TL cinsindendir. Diger saglayicilar varsayilan {pricing.pricing_tl.default} TL.</p>
          </div>
        )}

        {/* Recent downloads */}
        {recent.length > 0 && (
          <div className="aurora-card rounded-3xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Son Indirmeler</h3>
              <Link to="/history" className="text-xs text-fuchsia-400 hover:text-fuchsia-300">Tumu &rarr;</Link>
            </div>
            <div className="space-y-2">
              {recent.map(d => (
                <div key={d.download_id} className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/5">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{d.item_name || d.source_link}</p>
                    <p className="text-xs text-muted-foreground capitalize">{d.provider} &middot; {new Date(d.downloaded_at).toLocaleString('tr-TR')}</p>
                  </div>
                  <span className="text-sm font-semibold text-emerald-400 tabular-nums ml-2">-{d.cost_tl} TL</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, cta, ctaTo, gradient, iconColor, testid }) {
  return (
    <div className={`aurora-card rounded-3xl p-6 bg-gradient-to-br ${gradient}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
      <div className="text-2xl font-bold mb-4 tabular-nums">{value}</div>
      <Link to={ctaTo} className={`inline-flex items-center gap-1 text-sm font-medium ${iconColor} hover:opacity-80`}>
        {cta} <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}
